import {
    Grid,
    IconButton,
    makeStyles,
    MenuItem,
    Select,
    TextField,
    Tooltip
} from '@material-ui/core';
import React, { useEffect, useRef } from 'react';
import useWhyDidYouUpdate from '../../hooks/useWhyDidYouUpdate';
import { DeleteIcon, DuplicateIcon, PlusIcon, TimesIcon } from '../../icons';
import EditableTextbox from '../atom/EditableTextbox';
import AutocompleteSelect from '../atom/AutocompleteSelect';

export const autocompleteStyleOverrides = {
    clearIndicator: {
        padding: '2.5px !important',
        right: '5px'
    },
    popupIndicator: {
        padding: '2.5px !important'
    }
};

export const useAutocompleteStyles = makeStyles(autocompleteStyleOverrides);

const generateHashChars = len => Math.floor(Math.random() * 16 ** len).toString(16);

const PriceItemRule = props => {
    const { rule, setRule, removeRule, duplicateRule, conditionTypes } = props;
    useWhyDidYouUpdate('PriceItem', props);
    const { conditions, net_usd, net_percentage, description } = rule;
    const nameElement = useRef(null);
    useEffect(() => {
        if (net_usd === 0 && nameElement?.current) {
            nameElement.current.focus();
        }
    }, [net_usd]);

    const RenderComponent = ({ condition, id }) => {
        const classes = useAutocompleteStyles();

        const conditionType = conditionTypes.find(t => condition.type === t.key);
        const { otherProps } = conditionType;
        const isAutocomplete = conditionType.renderComponent === AutocompleteSelect;

        return React.createElement(conditionType.renderComponent, {
            key: `${rule.id}-${id}-${condition.type}`,
            ...otherProps,
            value: condition.value,
            setValue: value =>
                setRule({
                    ...rule,
                    conditions: conditions.map((c, k) =>
                        k === id
                            ? {
                                  ...condition,
                                  value
                              }
                            : c
                    )
                }),
            classes: isAutocomplete
                ? {
                      ...classes,
                      ...otherProps.classes
                  }
                : undefined
        });
    };

    return (
        <Grid
            container
            item
            xs={12}
            style={{
                border: '1px solid #ccc',
                padding: 16,
                borderRadius: 4,
                marginBottom: 10,
                backgroundColor: '#ffffff'
            }}
        >
            <Grid container item xs={12} alignItems="center">
                <Grid item xs={6}>
                    <EditableTextbox
                        label="Rule description"
                        type="text"
                        placeholder="Add a rule description here!"
                        text={description}
                        setText={desc =>
                            setRule({
                                ...rule,
                                description: desc
                            })
                        }
                    >
                        <IconButton onClick={duplicateRule}>
                            <Tooltip title="Duplicate rule">
                                <DuplicateIcon />
                            </Tooltip>
                        </IconButton>
                        <IconButton onClick={() => removeRule(rule)}>
                            <Tooltip title="Remove rule">
                                <DeleteIcon />
                            </Tooltip>
                        </IconButton>
                    </EditableTextbox>
                </Grid>
                <Grid item container xs={6} spacing={2}>
                    <Grid item xs={6}>
                        <TextField
                            type="number"
                            inputRef={nameElement}
                            inputProps={{ step: 0.01 }}
                            label="Net USD"
                            fullWidth
                            value={net_usd}
                            onChange={event =>
                                setRule({
                                    ...rule,
                                    net_usd: parseFloat(event.target.value)
                                })
                            }
                        ></TextField>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            type="number"
                            inputProps={{ step: 0.01 }}
                            label="Net %"
                            fullWidth
                            value={net_percentage}
                            onChange={event =>
                                setRule({
                                    ...rule,
                                    net_percentage: parseFloat(event.target.value)
                                })
                            }
                        ></TextField>
                    </Grid>
                </Grid>
            </Grid>
            {conditions &&
                conditions.map((condition, key) => (
                    <Grid
                        container
                        item
                        xs={12}
                        key={condition.id}
                        alignItems="flex-end"
                        style={{
                            marginTop: 10,
                            border: '1px solid #eee',
                            borderRadius: 4,
                            padding: 8,
                            backgroundColor: '#fafafa'
                        }}
                    >
                        <Grid container item xs={12} sm={10} alignItems="flex-end">
                            <Select
                                value={condition.type}
                                onChange={event =>
                                    setRule({
                                        ...rule,
                                        conditions: conditions.map((c, k) =>
                                            k === key
                                                ? {
                                                      ...condition,
                                                      type: event.target.value,
                                                      value: conditionTypes.find(
                                                          type => event.target.value === type.key
                                                      ).defaultValue
                                                  }
                                                : c
                                        )
                                    })
                                }
                            >
                                {conditionTypes &&
                                    conditionTypes.map(type => (
                                        <MenuItem key={type.key} value={type.key}>
                                            {type.name}
                                        </MenuItem>
                                    ))}
                            </Select>
                            {conditionTypes &&
                                conditionTypes.find(t => condition.type === t.key) && (
                                    <RenderComponent condition={condition} id={key} />
                                )}
                        </Grid>
                        <Grid container item xs={12} sm={2} justifyContent="flex-end">
                            <IconButton
                                onClick={() =>
                                    conditions.length > 1
                                        ? setRule({
                                              ...rule,
                                              conditions: conditions.filter(
                                                  e => e.id !== condition.id
                                              )
                                          })
                                        : removeRule(rule)
                                }
                            >
                                <Tooltip title="Remove condition">
                                    <TimesIcon color="disabled" />
                                </Tooltip>
                            </IconButton>
                        </Grid>
                    </Grid>
                ))}
            <Grid item xs={12} style={{ marginTop: 8 }}>
                <IconButton
                    onClick={() =>
                        setRule({
                            ...rule,
                            conditions: conditions.concat({
                                id: generateHashChars(8),
                                type: 'usergroup',
                                value: []
                            })
                        })
                    }
                >
                    <Tooltip title="Add condition">
                        <PlusIcon color="disabled" />
                    </Tooltip>
                </IconButton>
            </Grid>
        </Grid>
    );
};

export default PriceItemRule;
